import {
	CONSTRUCT_MARKET_STATE_ITEM_URL_QUERY,
	FIND_ALERT_EXECUTION_NOTIFICATIONS_FOR_PERIOD,
	FIND_COMPANY_HOTEL_AVAILABILITY_WITH_COMPARISON_QUERY,
	FIND_COMPANY_HOTEL_PRICE_RECOMMENDATION_STATE_QUERY,
	FIND_LATEST_PRICE_RECOMMENDATION_STATES_FOR_STAT_DATE_QUERY,
	FIND_MARKET_STATE_DAY_PRICE_EVOLUTION_FOR_HISTORY_QUERY,
	FIND_MARKET_STATE_DAY_PRICE_EVOLUTION_FOR_TIMEFRAME_QUERY,
	FIND_MARKET_STATE_FOR_DAY_QUERY,
	FIND_MARKET_STATE_FOR_PERIOD_QUERY,
	FIND_PREVIOUS_MARKET_STATE_FOR_DAY_QUERY,
	MARK_ALERT_NOTIFICATION_AS_READ_MUTATION,
	TRIGGER_MARKET_STATE_UPDATE_MUTATION,
	TRIGGER_PRICE_RECOMMENDATION_LITE_UPDATE_MUTATION,
	TRIGGER_PRICE_RECOMMENDATION_UPDATE_MUTATION,
} from '@app/model/market_state/service/MarketStateGraphQLQueries';
import Return from '@app/model/_common/Return';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import Month from '@app/model/market_state/Month';
import { GraphQLExecutor } from '@app/model/_common/service/GraphQLExecutor';
import MarketStateForDayNotificationsOutput from '@app/model/market_state/MarketStateForDayNotificationsOutput';
import MarketStateSnapshotOutput from '@app/model/market_state/output/MarketStateSnapshotOutput';
import MarketStateDailySnapshotWithComparisonOutput
	from '@app/model/market_state/output/MarketStateDailySnapshotWithComparisonOutput';
import { MarketStateStatDateSnapshot } from '@app/model/market_state/MarketStateStatDateSnapshot';
import CompanyHotelAvailabilityStateWithComparisonOutput
	from '@app/model/market_state/CompanyHotelAvailabilityStateWithComparisonOutput';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import _isEmpty from 'lodash/isEmpty';
import ProcessRequestExecutionState from '@app/model/process-request/ProcessRequestExecutionState';
import { HttpService } from '@app/model/_common/service/HttpService';


class MarketStateService {
	public static findMarketStateProof(companyHotelId: number, proofChoiceId: number) {
		return HttpService.get<string>(`/api/market-state/${companyHotelId}/proof/choice/${proofChoiceId}`);
	}

	public static triggerMarketStateUpdate(variables: {
		companyHotelId: number,
		year: number,
		month: Month,
		numberOfMonths: number,
		filterInput: MarketStateFilterInput,
		withOta: boolean,
		withProof: boolean
	}) {
		return GraphQLExecutor.execute<ProcessRequestExecutionState>(TRIGGER_MARKET_STATE_UPDATE_MUTATION, variables);
	}

	public static findMarketStateForDay(variables: {
		companyHotelId: number,
		statDate: string,
		filterInput: MarketStateFilterInput
	}) {
		return GraphQLExecutor.execute<MarketStateDailySnapshotWithComparisonOutput>(FIND_MARKET_STATE_FOR_DAY_QUERY, variables);
	}

	public static async findPreviousMarketStateForDay(variables: {
		companyHotelId: number,
		statDate: string,
		filterInput: MarketStateFilterInput,
		fromDate: string
	}): Promise<MarketStateStatDateSnapshot | undefined> {
		const result = await GraphQLExecutor.execute<MarketStateStatDateSnapshot | null>(FIND_PREVIOUS_MARKET_STATE_FOR_DAY_QUERY, variables);
		return result ?? undefined;
	}


	public static findMarketStateForPeriod(variables: {
		companyHotelId: number,
		year: number,
		month: Month,
		startStatDate?: string | null,
		endStatDate?: string | null,
		filterInput: MarketStateFilterInput
	}) {
		return GraphQLExecutor.execute<MarketStateSnapshotOutput>(FIND_MARKET_STATE_FOR_PERIOD_QUERY, variables);
	}

	public static markAlertExecutionNotificationAsRead(variables: { companyHotelId: number, notificationId: number }) {
		return GraphQLExecutor.execute<Return>(MARK_ALERT_NOTIFICATION_AS_READ_MUTATION, variables);
	}

	public static findMarketStateDayPriceEvolutionForHistory(variables: {
		companyHotelId: number,
		statDate: string,
		filterInput: MarketStateFilterInput
	}) {
		return GraphQLExecutor.execute<MarketStateStatDateSnapshot[]>(FIND_MARKET_STATE_DAY_PRICE_EVOLUTION_FOR_HISTORY_QUERY, variables);
	}

	public static findMarketStateDayPriceEvolutionForTimeframe(variables: {
		companyHotelId: number,
		statDate: string,
		filterInput: MarketStateFilterInput
	}) {
		return GraphQLExecutor.execute<MarketStateStatDateSnapshot[]>(FIND_MARKET_STATE_DAY_PRICE_EVOLUTION_FOR_TIMEFRAME_QUERY, variables);
	}

	public static findAlertExecutionNotificationsForPeriod(variables: {
		companyHotelId: number,
		startStatDate: string,
		endStatDate: string
	}) {
		return GraphQLExecutor.execute<MarketStateForDayNotificationsOutput[]>(FIND_ALERT_EXECUTION_NOTIFICATIONS_FOR_PERIOD, variables);
	}

	public static findCompanyHotelAvailabilityWithComparison(variables: {
		companyHotelId: number,
		statDate: string
	}) {
		return GraphQLExecutor.execute<CompanyHotelAvailabilityStateWithComparisonOutput>(FIND_COMPANY_HOTEL_AVAILABILITY_WITH_COMPARISON_QUERY, variables);
	}

	public static constructMarketStateItemUrl(marketStateItemId: number, marketStateIds: number[]) {
		return GraphQLExecutor.execute<string>(CONSTRUCT_MARKET_STATE_ITEM_URL_QUERY, {
			marketStateItemId,
			marketStateIds,
		});
	}

	public static triggerPriceRecommendationUpdate(variables: { companyHotelId: number, year: number, month: Month }) {
		return GraphQLExecutor.execute<ProcessRequestExecutionState>(TRIGGER_PRICE_RECOMMENDATION_UPDATE_MUTATION, variables);
	}


	public static triggerPriceRecommendationLiteUpdate(variables: {
		companyHotelId: number,
		year: number,
		month: Month
	}) {
		return GraphQLExecutor.execute<ProcessRequestExecutionState>(TRIGGER_PRICE_RECOMMENDATION_LITE_UPDATE_MUTATION, variables);
	}

	public static async findLatestPriceRecommendationStateForStatDate(
		companyHotelId: number,
		statDate: string
	) {
		const states = await GraphQLExecutor.execute<PriceRecommendationState[]>(FIND_LATEST_PRICE_RECOMMENDATION_STATES_FOR_STAT_DATE_QUERY, {companyHotelId, statDate});
		return !_isEmpty(states) ? states.map(state => new PriceRecommendationState(state)) : [];
	}

	public static async findCompanyHotelPriceRecommendationState(
		companyHotelId: number,
		recommendationStateId: number
	) {
		const state = await GraphQLExecutor.execute<PriceRecommendationState | null>(
			FIND_COMPANY_HOTEL_PRICE_RECOMMENDATION_STATE_QUERY,
			{ companyHotelId, recommendationStateId }
		);
		return state ? new PriceRecommendationState(state) : null;
	}


}

export default MarketStateService;
